import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import EmployeeService from '../../services/employee.service';
import { useTranslation } from '../../hooks/useTranslation';

const defaultForm = {
  name: '',
  email: '',
  phone: '',
  role: 'cleaner',
  status: 'active',
  hourly_rate: '',
  overtime_rate: '',
  max_hours_per_day: 8,
  max_hours_per_week: 40,
  color: '#667eea',
  hire_date: '',
};

const EmployeeForm = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(defaultForm);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { t } = useTranslation();

  useEffect(() => {
    if (isEdit) {
      fetchEmployee();
    }
  }, [id, isEdit]);

  const fetchEmployee = async () => {
    setLoading(true);
    try {
      const response = await EmployeeService.get(id);
      const emp = response.data?.data;
      setFormData({
        name: emp.name || '',
        email: emp.email || '',
        phone: emp.phone || '',
        role: emp.role || 'cleaner',
        status: emp.status || 'active',
        hourly_rate: emp.hourly_rate != null ? String(emp.hourly_rate) : '',
        overtime_rate: emp.overtime_rate != null ? String(emp.overtime_rate) : '',
        max_hours_per_day: emp.max_hours_per_day ?? 8,
        max_hours_per_week: emp.max_hours_per_week ?? 40,
        color: emp.color || '#667eea',
        hire_date: emp.hire_date ? emp.hire_date.slice(0, 10) : '',
      });
    } catch (error) {
      console.error('Error loading employee:', error);
      showSnackbar(t('employees.errorLoadingEmployee'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const buildPayload = () => {
    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim() || null,
      phone: formData.phone.trim() || null,
      role: formData.role,
      status: formData.status,
      hourly_rate: parseFloat(formData.hourly_rate),
      max_hours_per_day: parseInt(String(formData.max_hours_per_day), 10),
      max_hours_per_week: parseInt(String(formData.max_hours_per_week), 10),
      color: formData.color,
      hire_date: formData.hire_date || null,
    };
    if (formData.overtime_rate === '' || formData.overtime_rate == null) {
      payload.overtime_rate = null;
    } else {
      payload.overtime_rate = parseFloat(formData.overtime_rate);
    }
    return payload;
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      showSnackbar(t('employees.pleaseFillName'), 'error');
      return;
    }
    const payload = buildPayload();
    if (Number.isNaN(payload.hourly_rate) || payload.hourly_rate < 0) {
      showSnackbar(t('employees.invalidHourlyRate'), 'error');
      return;
    }
    if (
      Number.isNaN(payload.max_hours_per_day) ||
      Number.isNaN(payload.max_hours_per_week)
    ) {
      showSnackbar(t('employees.invalidHours'), 'error');
      return;
    }
    if (payload.overtime_rate != null && Number.isNaN(payload.overtime_rate)) {
      showSnackbar(t('employees.invalidOvertimeRate'), 'error');
      return;
    }

    try {
      setLoading(true);
      if (isEdit) {
        await EmployeeService.update(id, payload);
        showSnackbar(t('employees.employeeUpdatedSuccess'));
      } else {
        await EmployeeService.create(payload);
        showSnackbar(t('employees.employeeCreatedSuccess'));
      }
      navigate('/employees');
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        (error.response?.data?.errors && JSON.stringify(error.response.data.errors)) ||
        error.message;
      showSnackbar(`${isEdit ? t('common.update') : t('common.save')}: ${msg}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={() => navigate('/employees')}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5">
              {isEdit ? t('employees.editEmployee') : t('employees.newEmployee')}
            </Typography>
          </Box>
        }
        action={
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={loading}
          >
            {isEdit ? t('common.update') : t('common.save')}
          </Button>
        }
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label={t('employees.name')}
              name="name"
              value={formData.name}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel id="employee-role-label">{t('employees.role')}</InputLabel>
              <Select
                labelId="employee-role-label"
                label={t('employees.role')}
                name="role"
                value={formData.role}
                onChange={handleChange}
              >
                <MenuItem value="cleaner">{t('employees.roleCleaner')}</MenuItem>
                <MenuItem value="supervisor">{t('employees.roleSupervisor')}</MenuItem>
                <MenuItem value="admin">{t('employees.roleAdmin')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel id="employee-status-label">{t('employees.status')}</InputLabel>
              <Select
                labelId="employee-status-label"
                label={t('employees.status')}
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <MenuItem value="active">{t('employees.statusActive')}</MenuItem>
                <MenuItem value="inactive">{t('employees.statusInactive')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('employees.email')}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('employees.phone')}
              name="phone"
              value={formData.phone}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              required
              label={t('employees.hourlyRate')}
              name="hourly_rate"
              type="number"
              inputProps={{ min: 0, step: '0.01' }}
              value={formData.hourly_rate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label={t('employees.overtimeRate')}
              name="overtime_rate"
              type="number"
              inputProps={{ min: 0, step: '0.01' }}
              value={formData.overtime_rate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <TextField
              fullWidth
              label={t('employees.hireDate')}
              name="hire_date"
              type="date"
              value={formData.hire_date}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('employees.maxHoursPerDay')}
              name="max_hours_per_day"
              type="number"
              inputProps={{ min: 1, max: 24 }}
              value={formData.max_hours_per_day}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('employees.maxHoursPerWeek')}
              name="max_hours_per_week"
              type="number"
              inputProps={{ min: 1, max: 168 }}
              value={formData.max_hours_per_week}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={8} lg={6}>
            <TextField
              fullWidth
              label={t('employees.color')}
              name="color"
              type="color"
              value={formData.color}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { xs: '100%', sm: 280 } }}
            />
          </Grid>
        </Grid>
      </CardContent>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default EmployeeForm;
